// slices/fundManagementInvestment/fundManagementConfigurationSlice.ts

import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import axiosInstance from 'Services/APIService';

// ======================== INTERFACES ========================

export interface OperationsConfig {
  id: string;
  unitType: 'incomeUnits' | 'growthUnits';
  perType: 'buy' | 'sell';
  percentage: number;
  groupId: string;
  serviceId: string;
  createdBy?: string;
  createdDate?: string;
  updatedBy?: string;
  updatedDate?: string;
}

export type ConfigAction = 'get' | 'update';

export interface ConfigPayload {
  action: ConfigAction;
  groupId: string;
  serviceId: string;
  unitType: 'incomeUnits' | 'growthUnits';
  perType: 'buy' | 'sell';
  percentage?: number;
}

export interface GetConfigResponse {
  data: OperationsConfig;
}

export interface UpdateConfigResponse {
  success: boolean;
  message: string;
}

export interface TrustConfigPayload {
  action: 'get' | 'update';
  groupId: string;
  serviceId: string;
  unitType?: 'incomeUnits' | 'growthUnits';
  isAdmin: boolean;
  investorEmail: string;
  trustName?: string;
  trustFundManagerSignatureBase64?: string | null;
  trustDeedExecutionDate?: string;
  investorSignatureBase64?: string | null;
}

export interface TrustConfigData {
  trustName?: string;
  trustFundManagerSignatureBase64?: string | null;
  trustDeedExecutionDate?: string;
  investorEmail?: string;
  investorSignatureBase64?: string | null;
  lastUpdatedBy?: string;
  lastUpdatedDateTime?: string;
  createdBy?: string;
  createdDateTime?: string;
  id?: string;
}

export interface TrustConfigResponse {
  data: {
    trustConfig: TrustConfigData | Record<string, never>;
    investorSignature: TrustConfigData | Record<string, never> | null;
  };
}

export interface UnitCertificate {
  id: string;
  unitType: 'growthUnits' | 'incomeUnits';
  investorsEmailId: string;
  investorName: string;
  investorAvatar: string;
  amountInvestedTillDate: string;
  totalUnitsHoldTillDate: string;
  createdDateTime: string;
  lastUpdatedDateTime: string;
  createdBy: string;
  lastmodifiedBy: string;
}

export interface CertificatePayload {
  groupId: string;
  serviceId: string;
  investorEmail: string;
  isAdmin?: boolean;
}

export interface CertificateResponse {
  status: string;
  data: {
    growthUnits: UnitCertificate[] | UnitCertificate | null;
    incomeUnits: UnitCertificate[] | UnitCertificate | null;
  };
}

// Daily Unit Price Average
export interface DailyPricePoint {
  date: string;
  averageUnitPrice: string;
  dataPoints: number;
  source: 'no_data' | 'actual' | 'previous_day';
}

export interface DailyUnitPricePayload {
  groupId: string;
  serviceId: string;
  unitType: 'growthUnits' | 'incomeUnits';
  fromDate: any;
  toDate: string;
}

export interface DailyUnitPriceResponse {
  status: string;
  data: {
    groupId: string;
    serviceId: string;
    unitType: string;
    fromDate: string;
    toDate: string;
    dailyAverages: DailyPricePoint[];
  };
}

// User Competency Check
export interface CompetencyPayload {
  groupId: string;
}

export interface CompetencyResponse {
  status: string;
  data: {
    userEmail: string;
    competency: 'competent' | 'incompetent';
    message: string;
  };
}

export interface YearlyReturnStats {
  currentUnitPrice: string;
  avgUnitPriceYear: string;
  unitsHeld: number;
  currentNAV: string;
  avgNAVYear: string;
  profit: string;
  pctReturn: string;
}

export interface YearlyReturnState {
  growthUnits: YearlyReturnStats | null;
  incomeUnits: YearlyReturnStats | null;
  loading: boolean;
  error: string | null;
  lastFetched?: string;
}

// NEW: Send Investor Certificates
export interface CertificateItem {
  investorEmail: string;
  investorName: string;
  trustName: string;
  certificateBase64: string;
  fundManagerName: string;
  unitsHeld: string;
  date: string;
  unitType: string;
  certificateNumber: string;
}

export interface SendCertificatesPayload {
  certificatesData: CertificateItem[];
}

export interface SendCertificatesResponse {
  status: 'success' | 'error';
  message: string;
  totalCertificates: number;
}

export interface GetYearlyReturnStatsPayload {
  groupId: string;
  serviceId: string;
  unitsHeld: number;
}

interface YearlyReturnResponse {
  status: 'success';
  data: {
    growthUnits: YearlyReturnStats;
    incomeUnits: YearlyReturnStats;
  };
}

// ======================== STATE ========================

export interface ConfigState {
  configs: OperationsConfig[];
  trustConfig: TrustConfigData | null;
  growthUnitsCertificates: UnitCertificate[];
  incomeUnitsCertificates: UnitCertificate[];

  dailyUnitPrices: DailyPricePoint[];
  dailyUnitPriceLoading: boolean;
  dailyUnitPriceError: string | null;

  userCompetency: 'competent' | 'incompetent' | null;
  competencyLoading: boolean;
  competencyError: string | null;

  // Send certificates state
  sendCertificatesLoading: boolean;
  sendCertificatesSuccess: boolean;
  sendCertificatesError: string | null;
  sendCertificatesCount: number;

  yearlyReturnStats: YearlyReturnState;

  loading: boolean;
  error: string | null;
  lastUpdated: string | null;
}

const initialState: ConfigState = {
  configs: [],
  trustConfig: null,
  growthUnitsCertificates: [],
  incomeUnitsCertificates: [],
  dailyUnitPrices: [],
  dailyUnitPriceLoading: false,
  dailyUnitPriceError: null,
  userCompetency: null,
  competencyLoading: false,
  competencyError: null,
  sendCertificatesLoading: false,
  sendCertificatesSuccess: false,
  sendCertificatesError: null,
  sendCertificatesCount: 0,
  yearlyReturnStats: {
    growthUnits: null,
    incomeUnits: null,
    loading: false,
    error: null
  },
  loading: false,
  error: null,
  lastUpdated: null
};

// ======================== THUNKS ========================

export const manageOperationsPercentage = createAsyncThunk(
  'config/manage',
  async (payload: ConfigPayload, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post(
        '/fund-investment-management/operations-percentage-set-up',
        payload
      );
      return {
        response: response.data,
        action: payload.action,
        unitType: payload.unitType,
        perType: payload.perType
      };
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to manage configuration');
    }
  }
);

export const manageTrustConfiguration = createAsyncThunk(
  'config/manageTrust',
  async (payload: TrustConfigPayload, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post<TrustConfigResponse>(
        '/fund-investment-management/trust-configurations',
        payload
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to manage trust configuration'
      );
    }
  }
);

export const getBuySellCertificate = createAsyncThunk(
  'config/getCertificate',
  async (payload: CertificatePayload, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post<CertificateResponse>(
        '/fund-investment-management/get-current-buy-sell-certificate',
        payload
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to fetch buy/sell certificate'
      );
    }
  }
);

export const getDailyUnitPriceAvg = createAsyncThunk(
  'config/getDailyUnitPriceAvg',
  async (payload: DailyUnitPricePayload, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post<DailyUnitPriceResponse>(
        '/fund-investment-management/get-daily-unit-price-avg',
        payload
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch daily unit prices');
    }
  }
);

export const checkUserCompetency = createAsyncThunk(
  'config/checkUserCompetency',
  async (payload: CompetencyPayload, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post<CompetencyResponse>(
        '/check-user-competency',
        payload
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to check user competency');
    }
  }
);

// NEW THUNK: Send Investor Certificates
export const sendInvestorCertificates = createAsyncThunk(
  'config/sendInvestorCertificates',
  async (payload: SendCertificatesPayload, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post<SendCertificatesResponse>(
        '/fund-investment-management/send-investors-certificates',
        payload
      );
      return response.data;
    } catch (error: any) {
      const msg = error.response?.data?.message || 'Failed to send investor certificates';
      return rejectWithValue(msg);
    }
  }
);

export const getUnitYearlyReturnStats = createAsyncThunk(
  'config/getUnitYearlyReturnStats',
  async (payload: GetYearlyReturnStatsPayload, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post<YearlyReturnResponse>(
        '/fund-investment-management/get_unit_yearly_return_stats',
        payload
      );

      if (response.data.status !== 'success') {
        return rejectWithValue('Failed to fetch return stats');
      }

      return response.data.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to fetch yearly return stats'
      );
    }
  }
);

// ======================== SLICE ========================

const fundManagementConfigurationSlice = createSlice({
  name: 'fundManagementConfiguration',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
      state.dailyUnitPriceError = null;
      state.competencyError = null;
      state.sendCertificatesError = null;
    },
    clearConfigs: (state) => {
      state.configs = [];
      state.error = null;
    },
    clearCertificates: (state) => {
      state.growthUnitsCertificates = [];
      state.incomeUnitsCertificates = [];
      state.error = null;
    },
    clearDailyUnitPrices: (state) => {
      state.dailyUnitPrices = [];
      state.dailyUnitPriceError = null;
    },
    clearCompetency: (state) => {
      state.userCompetency = null;
      state.competencyError = null;
    },
    clearSendCertificatesStatus: (state) => {
      state.sendCertificatesLoading = false;
      state.sendCertificatesSuccess = false;
      state.sendCertificatesError = null;
      state.sendCertificatesCount = 0;
    }
  },
  extraReducers: (builder) => {
    builder
      // Operations Percentage
      .addCase(manageOperationsPercentage.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(manageOperationsPercentage.fulfilled, (state, action) => {
        state.loading = false;
        const { response, action: operationType, unitType, perType } = action.payload;
        if (operationType === 'get' && response.data) {
          const config = response.data;
          state.configs = state.configs.filter(
            (c) => !(c.unitType === unitType && c.perType === perType)
          );
          state.configs.push(config);
          state.lastUpdated = new Date().toISOString();
        }
        if (operationType === 'update' && response.success) {
          state.lastUpdated = new Date().toISOString();
        }
      })
      .addCase(manageOperationsPercentage.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // Trust Configuration
      .addCase(manageTrustConfiguration.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(manageTrustConfiguration.fulfilled, (state, action) => {
        state.loading = false;
        const responseData = action.payload.data;

        let investorSignatureBase64: string | null = null;

        if (responseData?.trustConfig && Object.keys(responseData.trustConfig).length > 0) {
          investorSignatureBase64 =
            (responseData.trustConfig as TrustConfigData).investorSignatureBase64 || null;
        }

        if (
          !investorSignatureBase64 &&
          responseData?.investorSignature &&
          Object.keys(responseData.investorSignature).length > 0
        ) {
          investorSignatureBase64 =
            (responseData.investorSignature as TrustConfigData).investorSignatureBase64 || null;
        }

        if (responseData?.trustConfig && Object.keys(responseData.trustConfig).length > 0) {
          state.trustConfig = {
            ...(responseData.trustConfig as TrustConfigData),
            investorSignatureBase64
          };
        } else {
          state.trustConfig = {
            investorSignatureBase64,
            investorEmail:
              (responseData.investorSignature as TrustConfigData)?.investorEmail || undefined,
            lastUpdatedBy:
              (responseData.investorSignature as TrustConfigData)?.lastUpdatedBy || undefined,
            lastUpdatedDateTime:
              (responseData.investorSignature as TrustConfigData)?.lastUpdatedDateTime || undefined
          };
        }

        state.lastUpdated = new Date().toISOString();
      })
      .addCase(manageTrustConfiguration.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // Certificates (Buy/Sell)
      .addCase(getBuySellCertificate.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getBuySellCertificate.fulfilled, (state, action) => {
        state.loading = false;

        if (action.payload?.status === 'success' && action.payload.data) {
          const { growthUnits, incomeUnits } = action.payload.data;

          const normalizeToArray = (
            data: UnitCertificate[] | UnitCertificate | null
          ): UnitCertificate[] => {
            if (!data) return [];
            return Array.isArray(data) ? data : [data];
          };

          state.growthUnitsCertificates = normalizeToArray(growthUnits);
          state.incomeUnitsCertificates = normalizeToArray(incomeUnits);
          state.lastUpdated = new Date().toISOString();
        } else {
          state.growthUnitsCertificates = [];
          state.incomeUnitsCertificates = [];
          state.error = 'Invalid certificate response';
        }
      })
      .addCase(getBuySellCertificate.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
        state.growthUnitsCertificates = [];
        state.incomeUnitsCertificates = [];
      })

      // Daily Unit Price Average
      .addCase(getDailyUnitPriceAvg.pending, (state) => {
        state.dailyUnitPriceLoading = true;
        state.dailyUnitPriceError = null;
      })
      .addCase(getDailyUnitPriceAvg.fulfilled, (state, action) => {
        state.dailyUnitPriceLoading = false;
        if (action.payload?.status === 'success' && action.payload.data?.dailyAverages) {
          state.dailyUnitPrices = action.payload.data.dailyAverages;
        } else {
          state.dailyUnitPrices = [];
        }
      })
      .addCase(getDailyUnitPriceAvg.rejected, (state, action) => {
        state.dailyUnitPriceLoading = false;
        state.dailyUnitPriceError = action.payload as string;
        state.dailyUnitPrices = [];
      })

      // User Competency
      .addCase(checkUserCompetency.pending, (state) => {
        state.competencyLoading = true;
        state.competencyError = null;
      })
      .addCase(checkUserCompetency.fulfilled, (state, action) => {
        state.competencyLoading = false;
        if (action.payload?.status === 'success' && action.payload.data) {
          state.userCompetency = action.payload.data.competency;
        } else {
          state.userCompetency = null;
        }
      })
      .addCase(checkUserCompetency.rejected, (state, action) => {
        state.competencyLoading = false;
        state.competencyError = action.payload as string;
        state.userCompetency = null;
      })

      // NEW: Send Investor Certificates
      .addCase(sendInvestorCertificates.pending, (state) => {
        state.sendCertificatesLoading = true;
        state.sendCertificatesSuccess = false;
        state.sendCertificatesError = null;
        state.sendCertificatesCount = 0;
      })
      .addCase(sendInvestorCertificates.fulfilled, (state, action) => {
        state.sendCertificatesLoading = false;
        if (action.payload.status === 'success') {
          state.sendCertificatesSuccess = true;
          state.sendCertificatesCount = action.payload.totalCertificates;
        } else {
          state.sendCertificatesSuccess = false;
          state.sendCertificatesError = action.payload.message || 'Failed to send certificates';
        }
      })
      .addCase(sendInvestorCertificates.rejected, (state, action) => {
        state.sendCertificatesLoading = false;
        state.sendCertificatesSuccess = false;
        state.sendCertificatesError = action.payload as string;
        state.sendCertificatesCount = 0;
      })

      .addCase(getUnitYearlyReturnStats.pending, (state) => {
        state.yearlyReturnStats.loading = true;
        state.yearlyReturnStats.error = null;
      })
      .addCase(getUnitYearlyReturnStats.fulfilled, (state, action) => {
        state.yearlyReturnStats.loading = false;
        state.yearlyReturnStats.growthUnits = action.payload.growthUnits;
        state.yearlyReturnStats.incomeUnits = action.payload.incomeUnits;
        state.yearlyReturnStats.lastFetched = new Date().toISOString();
      })
      .addCase(getUnitYearlyReturnStats.rejected, (state, action) => {
        state.yearlyReturnStats.loading = false;
        state.yearlyReturnStats.error = action.payload as string;
        state.yearlyReturnStats.growthUnits = null;
        state.yearlyReturnStats.incomeUnits = null;
      });
  }
});

// ======================== ACTIONS ========================

export const {
  clearError,
  clearConfigs,
  clearCertificates,
  clearDailyUnitPrices,
  clearCompetency,
  clearSendCertificatesStatus
} = fundManagementConfigurationSlice.actions;

// ======================== SELECTORS ========================

export const selectAllConfigs = (state: any) => state.fundManagementConfiguration?.configs || [];

export const selectConfigByType =
  (unitType: 'incomeUnits' | 'growthUnits', perType: 'buy' | 'sell') => (state: any) => {
    return (
      state.fundManagementConfiguration?.configs?.find(
        (c: OperationsConfig) => c.unitType === unitType && c.perType === perType
      ) || null
    );
  };

export const selectLoading = (state: any) => state.fundManagementConfiguration?.loading || false;
export const selectError = (state: any) => state.fundManagementConfiguration?.error || null;
export const selectLastUpdated = (state: any) =>
  state.fundManagementConfiguration?.lastUpdated || null;

export const selectTrustConfig = (state: any) =>
  state.fundManagementConfiguration?.trustConfig || null;

export const selectGrowthUnitsCertificates = (state: any) =>
  state.fundManagementConfiguration?.growthUnitsCertificates || [];

export const selectIncomeUnitsCertificates = (state: any) =>
  state.fundManagementConfiguration?.incomeUnitsCertificates || [];

export const selectDailyUnitPrices = (state: any) =>
  state.fundManagementConfiguration?.dailyUnitPrices || [];

export const selectGrowthUnitPrices = (state: any) =>
  state.fundManagementConfiguration?.dailyUnitPrices || [];

export const selectIncomeUnitPrices = (state: any) =>
  state.fundManagementConfiguration?.dailyUnitPrices || [];

export const selectDailyUnitPriceLoading = (state: any) =>
  state.fundManagementConfiguration?.dailyUnitPriceLoading || false;

export const selectDailyUnitPriceError = (state: any) =>
  state.fundManagementConfiguration?.dailyUnitPriceError || null;

export const selectUserCompetency = (state: any) =>
  state.fundManagementConfiguration?.userCompetency || null;

export const selectCompetencyLoading = (state: any) =>
  state.fundManagementConfiguration?.competencyLoading || false;

export const selectCompetencyError = (state: any) =>
  state.fundManagementConfiguration?.competencyError || null;

// New selectors for send certificates
export const selectSendCertificatesLoading = (state: any) =>
  state.fundManagementConfiguration?.sendCertificatesLoading || false;

export const selectSendCertificatesSuccess = (state: any) =>
  state.fundManagementConfiguration?.sendCertificatesSuccess || false;

export const selectSendCertificatesError = (state: any) =>
  state.fundManagementConfiguration?.sendCertificatesError || null;

export const selectSendCertificatesCount = (state: any) =>
  state.fundManagementConfiguration?.sendCertificatesCount || 0;

export const selectYearlyReturnStats = (state: any) =>
  state.fundManagementConfiguration?.yearlyReturnStats || {
    growthUnits: null,
    incomeUnits: null,
    loading: false,
    error: null
  };

export const selectGrowthYearlyReturn = (state: any) =>
  state.fundManagementConfiguration?.yearlyReturnStats?.growthUnits || null;

export const selectIncomeYearlyReturn = (state: any) =>
  state.fundManagementConfiguration?.yearlyReturnStats?.incomeUnits || null;

export const selectYearlyReturnLoading = (state: any) =>
  state.fundManagementConfiguration?.yearlyReturnStats?.loading || false;

export const selectYearlyReturnError = (state: any) =>
  state.fundManagementConfiguration?.yearlyReturnStats?.error || null;

export default fundManagementConfigurationSlice.reducer;
